import { useState, useEffect } from "react";
import { Modal, Spinner, InputGroup, Form } from "react-bootstrap";
import { toast } from 'react-toastify';
import { spinner } from "../utils/Helper";
import {
    getFirestore, collection, getDocs, doc, addDoc, updateDoc, query, where, orderBy, limit, Timestamp
} from 'firebase/firestore';

import { LuUserPlus } from "react-icons/lu";
import check from "../images/check.png";
import expFail from '../images/expFail.png';

export default function AccessVitals(props) {
    const db = getFirestore();
    const [accessItems, setAccessItems] = useState([]);
    const [guestItems, setGuestItems] = useState([]);
    const [showGuestModal, setShowGuestModal] = useState(false);
    const [newGuestName, setNewGuestName] = useState("");
    const [isAddingGuest, setIsAddingGuest] = useState(false);
    const [memberEmail, setMemberEmail] = useState("");

    // Compute active current log collection name dynamically
    const getLogsCollectionName = (date = new Date()) => {
        const year = date instanceof Date ? date.getFullYear() : new Date().getFullYear();
        return `access_logs_${year}`;
    };

    useEffect(() => {
        let email = props.memberEmail;
        if (!email || email === "Login") {
            email = localStorage.getItem('email');
        }
        if (email && email !== "Login") {
            setMemberEmail(email);
            getAccessDoc(email);
        }
    }, [props.memberEmail, props.refreshTrigger]);

    // --- Helper: Fetch Active Guests ---
    const fetchGuests = async (email) => {
        const logsRef = collection(db, getLogsCollectionName());
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        
        const q = query(
            logsRef,
            where("signedInBy", "==", email.toLowerCase()),
            where("entryType", "==", "Guest"),
            where("scannedAt", ">=", Timestamp.fromDate(startOfToday)),
            orderBy("scannedAt", "desc")
        );

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(guest => guest.status !== "cancelled");
    };

    // --- Main: Load Member Status & Guest Count ---
    const getAccessDoc = async (identifier) => {
        if (!identifier) return;
        const logsRef = collection(db, getLogsCollectionName());
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        try {
            const qMember = query(
                logsRef,
                where("rawPayload", "==", identifier.toLowerCase()),
                where("scannedAt", ">=", Timestamp.fromDate(startOfToday)),
                orderBy("scannedAt", "desc"),
                limit(1)
            );
            const snapMember = await getDocs(qMember);

            if (!snapMember.empty) {
                const docData = snapMember.docs[0].data();
                const activeGuests = await fetchGuests(identifier);

                setAccessItems([{
                    id: snapMember.docs[0].id,
                    ...docData,
                    guestCount: activeGuests.length
                }]);
            } else {
                setAccessItems([]);
            }
        } catch (e) {
            console.error("Query Error:", e);
        }
    };

    // --- Action: Show Guest Modal ---
    const handleShowGuests = async (email) => {
        if (!email) return;
        spinner(true);
        try {
            const guests = await fetchGuests(email);
            setGuestItems(guests);
            setShowGuestModal(true);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load guests");
        } finally {
            spinner(false);
        }
    };

    // --- Action: Add Guest ---
    const handleAddGuest = async (email) => {
        if (!newGuestName.trim()) return toast.warning("Enter a name");
        setIsAddingGuest(true);
        try {
            const nameParts = newGuestName.trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "";

            const bannedQuery = query(
                collection(db, "banned_list"),
                where("firstName", "==", firstName),
                where("lastName", "==", lastName)
            );
            const bannedSnapshot = await getDocs(bannedQuery);

            if (!bannedSnapshot.empty) {
                toast.error("The person you are trying to add is on a banned list.");
                setIsAddingGuest(false);
                return;
            }

            await addDoc(collection(db, getLogsCollectionName()), {
                fullName: newGuestName.trim(),
                entryType: "Guest",
                method: "Self Check In",
                signedInBy: email.toLowerCase(),
                rawPayload: email.toLowerCase(),
                scannedAt: Timestamp.now(),
                status: "active",
                isExpired: false,
                hasIncident: false,
                isFlagged: false
            });
            
            setNewGuestName("");
            const updated = await fetchGuests(email);
            setGuestItems(updated);

            setAccessItems(prev => prev.map(item =>
                item.rawPayload === email ? { ...item, guestCount: updated.length } : item
            ));
            toast.success("Guest added");
        } catch (e) {
            console.error(e);
            toast.error("Error adding guest");
        } finally {
            setIsAddingGuest(false);
        }
    };

    // --- Action: Cancel Guest ---
    const handleCancelGuest = async (guestId, email) => {
        if (!window.confirm("Cancel this guest check-in?")) return;
        spinner(true);
        try {
            await updateDoc(doc(db, getLogsCollectionName(), guestId), {
                status: "cancelled",
                cancelledAt: Timestamp.now()
            });
            const updated = await fetchGuests(email);
            setGuestItems(updated);

            setAccessItems(prev => prev.map(item =>
                item.rawPayload === email ? { ...item, guestCount: updated.length } : item
            ));
            toast.info("Guest cancelled");
        } finally {
            spinner(false);
        }
    };

    const formatDisplayValue = (str) => {
        if (!str) return "";
        if (str.includes('@')) return str.toLowerCase();
        return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    return (
        <div>
            {accessItems && Array.isArray(accessItems) && accessItems.map((x) => (
                <div key={x.id} className="position-relative d-inline-block hoverX animated fadeInRight" onClick={() => handleShowGuests(x.rawPayload)}>
                    {x.guestCount > 0 && (
                        <span className="badge rounded-pill bg-danger position-absolute"
                            style={{ top: '-20px', right: '0px', zIndex: 2, border: '2px solid white' }}>
                            {x.guestCount} {x.guestCount === 1 ? 'Guest' : 'Guests'}
                        </span>
                    )}

                    <button
                        className="btn"
                        onClick={() => handleShowGuests(x.rawPayload)}
                        style={{ borderRadius: '15px', position: 'relative' }}
                    >
                        <img src={x.isExpired ? expFail : check} className="check" alt="status" />
                        <br />
                        <span style={{ display: 'block', marginTop: '5px' }}>
                            {x.isExpired ? <span className="orange"> Expired</span> : ""}
                        </span>
                        {!x.isExpired && (
                            <span>{x.scannedAt?.toDate().toLocaleDateString()}</span>
                        )}
                    </button>
                </div>
            ))}

            {/* --- RE-STYLED GUEST MODAL MATCHING QRWEBSCAN ENVIRONMENT --- */}
            <Modal show={showGuestModal} onHide={() => setShowGuestModal(false)} centered contentClassName="qr-modal-content bg-dark-profile" backdropClassName='bg-dark'>
                <div className="qr-modal-success-bar" style={{ background: 'var(--brand-primary)' }}></div>
                <Modal.Body className="p-4 text-white">
                    <h2 className="fw-bold mb-1 text-center">Guest Management</h2>
                    <p className="qr-subtext small text-center mb-4 opacity-50">Sponsor: {formatDisplayValue(accessItems[0]?.fullName || memberEmail)}</p>

                    <div className="qr-guest-section mb-4">
                        <InputGroup className="mb-2">
                            <Form.Control
                                placeholder="Guest Name..."
                                className="qr-input"
                                value={newGuestName}
                                onChange={(e) => setNewGuestName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddGuest(accessItems[0]?.rawPayload || memberEmail)}
                            />
                            <button
                                className="qr-btn qr-btn-primary"
                                onClick={() => handleAddGuest(accessItems[0]?.rawPayload || memberEmail)}
                                disabled={isAddingGuest}
                            >
                                {isAddingGuest ? <Spinner size="sm" /> : "ADD"}
                            </button>
                        </InputGroup>
                    </div>

                    <div className="qr-card-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        {guestItems.map((guest) => (
                            <div key={guest.id} className="qr-log-card py-2 d-flex justify-content-between align-items-center mb-2" style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '6px' }}>
                                <div className="fw-bold small ps-2 text-white">
                                    {formatDisplayValue(guest.fullName)}
                                </div>
                                <button
                                    className="btn btn-sm text-danger fw-bold border-0 bg-transparent"
                                    onClick={() => handleCancelGuest(guest.id, accessItems[0]?.rawPayload || memberEmail)}
                                >
                                    REMOVE
                                </button>
                            </div>
                        ))}
                        {guestItems.length === 0 && (
                            <div className="text-center py-4 qr-subtext text-white-50">No active guests.</div>
                        )}
                    </div>
                    <button className="qr-btn qr-btn-warning w-100 mt-4" onClick={() => setShowGuestModal(false)}>Close</button>
                </Modal.Body>
            </Modal>
        </div>
    );
}